import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Alert,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { VENDOR_CATEGORIES } from "../../utils/constants";
import { styles } from "../../styles/Vendors.styles";
import VendorOwnerSelect from "../../components/VendorOwnerSelect";

const emptyForm = {
  name: "",
  website: "",
  category: "",
  owner: "",
  services: "",
  contactName: "",
  contactEmail: "",
  trustCenterUrl: "",
  authType: "SSO / SAML",
  contractStart: "",
  contractEnd: "",
};

const AddVendorDialog = ({ open, onClose, onSubmit, onGoToDiscovered, people = [] }) => {
  const theme = useTheme();
  const [mode, setMode] = useState("choice");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const handleClose = () => {
    setMode("choice");
    setForm(emptyForm);
    setErrors({});
    onClose();
  };

  const handleField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Vendor name is required";
    if (!form.category) nextErrors.category = "Select a category";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onSubmit({ ...form, name: form.name.trim() });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "16px",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        {mode === "manual" && (
          <IconButton size="small" onClick={() => setMode("choice")} sx={{ mr: 1, ml: -1 }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {mode === "choice" ? "Add Vendor" : "Add Vendor Manually"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ background: theme.customColors.background.main }}>
        {mode === "choice" ? (
          <>
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: theme.customColors.background.info,
                border: `1px solid ${theme.customColors.border.accent}`,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ fontSize: "14px", color: theme.customColors.text.strong }}
              >
                Choose how you would like to add a vendor to your register.
              </Typography>
            </Alert>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    cursor: "pointer",
                    border: `1px solid ${theme.customColors.border.main}`,
                    borderRadius: "12px",
                    background: theme.customColors.background.main,
                    boxShadow: theme.customColors.shadow.main,
                    transition: "all 0.2s ease",
                    height: "100%",
                    "&:hover": {
                      borderColor: theme.palette._blue.main,
                      backgroundColor: theme.customColors.background.hover,
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                  onClick={onGoToDiscovered}
                >
                  <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          backgroundColor: theme.palette._blue.light,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LinkIcon sx={{ fontSize: 20, color: theme.palette._blue.main }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          sx={{ fontSize: "15px", color: theme.customColors.text.strong, mb: 0.5 }}
                        >
                          Connect via Integration
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px", lineHeight: 1.3 }}>
                          Review vendors auto-discovered from connected systems (SSO, cloud, HRIS) and add the ones you use.
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    cursor: "pointer",
                    border: `1px solid ${theme.customColors.border.main}`,
                    borderRadius: "12px",
                    background: theme.customColors.background.main,
                    boxShadow: theme.customColors.shadow.main,
                    transition: "all 0.2s ease",
                    height: "100%",
                    "&:hover": {
                      borderColor: theme.palette._blue.main,
                      backgroundColor: theme.customColors.background.hover,
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                  onClick={() => setMode("manual")}
                >
                  <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          backgroundColor: theme.palette._blue.light,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <AddIcon sx={{ fontSize: 20, color: theme.palette._blue.main }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          sx={{ fontSize: "15px", color: theme.customColors.text.strong, mb: 0.5 }}
                        >
                          Add Manually
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px", lineHeight: 1.3 }}>
                          Enter vendor details yourself to start tracking and assessing their risk.
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Vendor name"
                placeholder="e.g. Acme Inc."
                required
                fullWidth
                size="small"
                value={form.name}
                onChange={handleField("name")}
                error={!!errors.name}
                helperText={errors.name}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Website"
                placeholder="https://acme.com"
                fullWidth
                size="small"
                value={form.website}
                onChange={handleField("website")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category"
                required
                select
                fullWidth
                size="small"
                value={form.category}
                onChange={handleField("category")}
                error={!!errors.category}
                helperText={errors.category}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>Select category</em>
                </MenuItem>
                {VENDOR_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VendorOwnerSelect
                value={form.owner}
                onChange={(email) => {
                  setForm((prev) => ({ ...prev, owner: email }));
                  setErrors((prev) => ({ ...prev, owner: undefined }));
                }}
                people={people}
                label="Vendor owner"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Trust Center URL"
                placeholder="https://trust.acme.com"
                fullWidth
                size="small"
                value={form.trustCenterUrl}
                onChange={handleField("trustCenterUrl")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      {mode === "manual" && (
        <DialogActions sx={{ px: 3, py: 2, background: theme.customColors.background.cardHeader }}>
          <Button onClick={handleClose} sx={{ textTransform: "none", color: theme.customColors.text.secondary }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} sx={styles.addButton}>
            Add Vendor
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AddVendorDialog;
